import type { MaterialsItemUsage } from "@/lib/materials-api";
import { brandSitePath } from "@/lib/brand-site-routing";

/** 从多选集合解析素材 id（排除 folder: 前缀的目录项） */
export function selectedMaterialItemIds(selected: Iterable<string>): number[] {
  const ids: number[] = [];
  for (const key of selected) {
    if (key.startsWith("folder:")) continue;
    const id = Number(key);
    if (Number.isInteger(id) && id > 0) ids.push(id);
  }
  return ids;
}

export type MaterialsBulkDeleteToast = {
  tone: "success" | "warning" | "error";
  message: string;
};

/** 根据批量删除 API 结果生成 toast 文案 */
export function summarizeMaterialsBulkDelete(result: {
  deleted: Array<{ name: string }>;
  blocked: Array<{ name: string; usages?: MaterialsItemUsage[] }>;
  failed: Array<{ id: number }>;
}): MaterialsBulkDeleteToast {
  const deletedCount = result.deleted.length;
  const blockedCount = result.blocked.length;
  const failedCount = result.failed.length;

  if (deletedCount === 0 && blockedCount === 0 && failedCount > 0) {
    return { tone: "error", message: "暂时无法确认引用，请稍后重试" };
  }

  if (deletedCount === 0 && blockedCount > 0) {
    const first = result.blocked[0];
    const reason = first?.usages?.length
      ? formatMaterialUsagesBlockReason(first.usages)
      : `「${first?.name ?? "素材"}」仍被引用，无法删除`;
    const suffix = blockedCount > 1 ? `等 ${blockedCount} 项无法删除` : "";
    return { tone: "warning", message: `${reason}${suffix}` };
  }

  if (deletedCount > 0 && blockedCount === 0 && failedCount === 0) {
    return {
      tone: "success",
      message: deletedCount === 1 ? `已删除「${result.deleted[0]?.name ?? "素材"}」` : `已删除 ${deletedCount} 项素材`,
    };
  }

  const parts: string[] = [];
  if (deletedCount > 0) parts.push(`已删除 ${deletedCount} 项`);
  if (blockedCount > 0) parts.push(`${blockedCount} 项仍被引用`);
  if (failedCount > 0) parts.push(`${failedCount} 项校验失败`);
  return { tone: deletedCount > 0 ? "warning" : "error", message: parts.join("，") };
}

/** 复制素材编号成功 toast 文案 */
export function materialsPublicIdCopySuccessMessage(publicId: string): string {
  return `已复制素材编号：${publicId}`;
}

/** 列表行：优先页面标题，否则 context / slug */
export function materialUsagePageLabel(usage: MaterialsItemUsage): string {
  const title = usage.page_title?.trim();
  if (title) return title;
  const slug = usage.page_slug?.trim();
  if (slug && slug !== "index") return slug;
  const ctx = usage.context?.trim();
  if (ctx?.startsWith("page:/")) {
    const token = ctx.slice("page:/".length).trim();
    return token || "首页";
  }
  return ctx || "站点首页";
}

export type MaterialUsageListRow = {
  key: string;
  siteTitle: string;
  pageLabel: string;
  frontendUrl: string | null;
  backendPath: string | null;
};

/** 预览弹窗引用列表行（标题优先 + 前台 URL + 后台路径） */
export function buildMaterialUsageListRows(
  usages: MaterialsItemUsage[],
  accountId: string,
): MaterialUsageListRow[] {
  return usages.map((usage, index) => {
    const siteTitle = usage.title?.trim() || `站点 #${usage.brand_site_id}`;
    const frontendUrl = usage.frontend_url?.trim() || usage.public_url?.trim() || null;
    let backendPath: string | null = null;
    if (usage.brand_id != null && usage.brand_id > 0 && usage.brand_site_id > 0) {
      const base = brandSitePath(accountId, usage.brand_id, usage.brand_site_id, "edit");
      const pageSlug = usage.page_slug?.trim();
      backendPath =
        pageSlug && pageSlug !== "index"
          ? `${base}?page=${encodeURIComponent(pageSlug)}`
          : base;
    }
    return {
      key: `${usage.brand_site_id}-${usage.context}-${usage.asset_id ?? index}`,
      siteTitle,
      pageLabel: materialUsagePageLabel(usage),
      frontendUrl,
      backendPath,
    };
  });
}

/** 单条引用 →「站点名 · 页面」 */
export function formatMaterialUsageEntry(usage: MaterialsItemUsage): string {
  const site = usage.title?.trim() || `站点 #${usage.brand_site_id}`;
  const page = materialUsagePageLabel(usage);
  return `${site} · ${page}`;
}

/** 多条引用 → 中文顿号列表 */
export function formatMaterialUsageEntries(usages: MaterialsItemUsage[]): string[] {
  return usages.map(formatMaterialUsageEntry);
}

/** 有引用时删除禁用说明（完整一句；列表 UI 旁作摘要） */
export function formatMaterialUsagesBlockReason(usages: MaterialsItemUsage[]): string {
  if (usages.length === 0) return "";
  return `该素材仍被 ${usages.length} 处品牌站页面引用，无法删除`;
}

function isUsageRow(value: unknown): value is MaterialsItemUsage {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as MaterialsItemUsage).brand_site_id === "number"
  );
}

/** 从 DELETE 422 in_use 响应体解析 usages */
export function extractInUseUsages(payload: unknown): MaterialsItemUsage[] | null {
  if (typeof payload !== "object" || payload === null || !("error" in payload)) return null;
  const error = (payload as { error?: unknown }).error;
  if (typeof error !== "object" || error === null) return null;
  const record = error as { code?: unknown; usages?: unknown };
  if (record.code !== "in_use" || !Array.isArray(record.usages)) return null;
  return record.usages.filter(isUsageRow);
}
